<template>
  <AdminShell
    page-title="行为分析"
    page-subtitle="查看直播落地页行为、意向线索与 TikTok 回传监控。"
    active-menu="behavior-analysis"
  >
    <template #actions>
      <el-button icon="el-icon-refresh" :loading="analyticsLoading || monitoringLoading" @click="refreshData">
        刷新
      </el-button>
    </template>

    <section class="behavior-analysis">
      <article class="filter-panel">
        <el-form :inline="true" :model="filters" class="filter-form" @submit.native.prevent>
          <el-form-item label="时间范围">
            <el-date-picker
              v-model="filters.range"
              type="datetimerange"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              value-format="yyyy-MM-dd HH:mm:ss"
              align="right"
            />
          </el-form-item>
          <el-form-item label="直播间 ID">
            <el-input v-model.trim="filters.room_id" clearable placeholder="room_001" @keyup.enter.native="refreshData" />
          </el-form-item>
          <el-form-item label="落地页">
            <el-input v-model.trim="filters.content_name" clearable placeholder="landing_page_A" @keyup.enter.native="refreshData" />
          </el-form-item>
          <el-form-item label="广告活动">
            <el-input v-model.trim="filters.utm_campaign" clearable placeholder="summer" @keyup.enter.native="refreshData" />
          </el-form-item>
          <el-form-item label="来源">
            <el-select v-model="filters.utm_source" clearable placeholder="全部来源">
              <el-option label="TikTok" value="tiktok" />
              <el-option label="Facebook" value="facebook" />
              <el-option label="YouTube" value="youtube" />
              <el-option label="Pinterest" value="pinterest" />
            </el-select>
          </el-form-item>
          <el-form-item label="意向等级">
            <el-select v-model="filters.intent_level" clearable placeholder="全部等级">
              <el-option v-for="level in intentLevels" :key="level" :label="level" :value="level" />
            </el-select>
          </el-form-item>
          <el-form-item class="filter-actions">
            <el-button type="primary" :loading="analyticsLoading || monitoringLoading" @click="refreshData">查询</el-button>
            <el-button @click="resetFilters">重置</el-button>
          </el-form-item>
        </el-form>
      </article>

      <article class="kpi-grid">
        <div v-for="item in kpiItems" :key="item.key" class="kpi-card">
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
        </div>
      </article>

      <article class="content-grid">
        <section class="panel panel-main">
          <header class="panel-header">
            <div>
              <h2>直播意向分析</h2>
              <p>按落地页、广告活动和直播间聚合线索指标。</p>
            </div>
            <el-tag type="info" effect="plain">{{ analyticsItems.length }} 条</el-tag>
          </header>

          <el-alert
            v-if="analyticsError"
            :title="analyticsError"
            type="error"
            :closable="false"
            show-icon
            class="panel-alert"
          />

          <el-table
            v-loading="analyticsLoading"
            :data="analyticsItems"
            border
            stripe
            class="analysis-table"
          >
            <el-table-column prop="content_name" label="落地页" min-width="170" show-overflow-tooltip>
              <template slot-scope="{ row }">{{ row.content_name || '--' }}</template>
            </el-table-column>
            <el-table-column prop="utm_campaign" label="广告活动" min-width="150" show-overflow-tooltip>
              <template slot-scope="{ row }">{{ row.utm_campaign || '--' }}</template>
            </el-table-column>
            <el-table-column prop="room_id" label="直播间" min-width="150" show-overflow-tooltip>
              <template slot-scope="{ row }">{{ row.room_id || '--' }}</template>
            </el-table-column>
            <el-table-column prop="session_count" label="会话" min-width="90" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.session_count) }}</template>
            </el-table-column>
            <el-table-column prop="visitor_count" label="访客" min-width="90" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.visitor_count) }}</template>
            </el-table-column>
            <el-table-column prop="page_view_count" label="PageView" min-width="105" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.page_view_count) }}</template>
            </el-table-column>
            <el-table-column prop="scroll_50_count" label="滚动 50%" min-width="105" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.scroll_50_count) }}</template>
            </el-table-column>
            <el-table-column prop="scroll_75_count" label="滚动 75%" min-width="105" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.scroll_75_count) }}</template>
            </el-table-column>
            <el-table-column prop="deep_180_count" label="180s" min-width="90" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.deep_180_count) }}</template>
            </el-table-column>
            <el-table-column prop="cta_count" label="CTA" min-width="90" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.cta_count) }}</template>
            </el-table-column>
            <el-table-column prop="contact_count" label="Contact" min-width="100" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.contact_count) }}</template>
            </el-table-column>
            <el-table-column prop="qualified_lead_count" label="Qualified Lead" min-width="140" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.qualified_lead_count) }}</template>
            </el-table-column>
            <el-table-column prop="avg_lead_score" label="平均分" min-width="100" align="right">
              <template slot-scope="{ row }">{{ formatDecimal(row.avg_lead_score) }}</template>
            </el-table-column>
            <el-table-column prop="intent_rate" label="意向率" min-width="100" align="right">
              <template slot-scope="{ row }">{{ formatPercent(row.intent_rate) }}</template>
            </el-table-column>
            <el-table-column prop="contact_rate" label="联系率" min-width="100" align="right">
              <template slot-scope="{ row }">{{ formatPercent(row.contact_rate) }}</template>
            </el-table-column>
            <template slot="empty">
              <el-empty description="暂无直播意向分析数据" />
            </template>
          </el-table>
        </section>

        <aside class="side-stack">
          <section class="panel">
            <header class="panel-header">
              <div>
                <h2>意向监控</h2>
                <p>事件、重复率与 TikTok CAPI 回传状态。</p>
              </div>
            </header>

            <el-alert
              v-if="monitoringError"
              :title="monitoringError"
              type="error"
              :closable="false"
              show-icon
              class="panel-alert"
            />

            <div v-loading="monitoringLoading" class="monitor-grid">
              <div v-for="item in monitoringItems" :key="item.key" class="monitor-item">
                <span>{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
              </div>
            </div>
          </section>

          <section class="panel">
            <header class="panel-header">
              <div>
                <h2>行为上报调试</h2>
                <p>手动提交一条行为事件，验证后端去重、线索分和 TikTok 映射。</p>
              </div>
            </header>

            <el-form ref="reportForm" :model="reportForm" :rules="reportRules" label-position="top" class="report-form">
              <el-form-item label="事件" prop="event">
                <el-select v-model="reportForm.event" class="full-field" placeholder="选择事件">
                  <el-option v-for="item in eventOptions" :key="item.value" :label="item.label" :value="item.value" />
                </el-select>
              </el-form-item>
              <el-form-item label="事件 ID" prop="event_id">
                <el-input v-model.trim="reportForm.event_id" placeholder="evt_xxx">
                  <el-button slot="append" @click="generateReportEventId">生成</el-button>
                </el-input>
              </el-form-item>
              <div class="report-pair">
                <el-form-item label="会话 ID">
                  <el-input v-model.trim="reportForm.session_id" clearable placeholder="sess_demo_001" />
                </el-form-item>
                <el-form-item label="用户 ID">
                  <el-input v-model.trim="reportForm.user_id" clearable placeholder="cid_demo_001" />
                </el-form-item>
              </div>
              <div class="report-pair">
                <el-form-item label="直播间 ID">
                  <el-input v-model.trim="reportForm.room_id" clearable placeholder="room_demo" />
                </el-form-item>
                <el-form-item label="落地页名称">
                  <el-input v-model.trim="reportForm.content_name" clearable placeholder="landing_page_A" />
                </el-form-item>
              </div>
              <el-form-item label="落地页 ID">
                <el-input v-model.trim="reportForm.content_id" clearable placeholder="lp_001" />
              </el-form-item>
              <el-form-item label="当前页面 URL">
                <el-input v-model.trim="reportForm.page_url" clearable placeholder="https://example.com/live?utm_source=tiktok" />
              </el-form-item>
              <el-form-item label="来源页面">
                <el-input v-model.trim="reportForm.referrer" clearable placeholder="https://www.tiktok.com/" />
              </el-form-item>
              <div class="report-pair">
                <el-form-item label="utm_source">
                  <el-input v-model.trim="reportForm.utm_source" clearable placeholder="tiktok" />
                </el-form-item>
                <el-form-item label="utm_campaign">
                  <el-input v-model.trim="reportForm.utm_campaign" clearable placeholder="summer" />
                </el-form-item>
              </div>
              <div class="report-pair">
                <el-form-item label="停留时间">
                  <el-input v-model.trim="reportForm.engagement_time" type="number" clearable placeholder="180">
                    <template slot="append">秒</template>
                  </el-input>
                </el-form-item>
                <el-form-item label="滚动深度">
                  <el-input v-model.trim="reportForm.scroll_percent" type="number" clearable placeholder="75">
                    <template slot="append">%</template>
                  </el-input>
                </el-form-item>
              </div>
              <div class="report-actions">
                <el-button type="primary" :loading="reporting" @click="submitReport">提交上报</el-button>
                <el-button @click="resetReportForm">重置</el-button>
              </div>
            </el-form>

            <el-alert
              v-if="reportError"
              :title="reportError"
              type="error"
              :closable="false"
              show-icon
              class="report-result"
            />

            <el-descriptions
              v-if="reportResult"
              :column="1"
              border
              size="small"
              class="report-result"
            >
              <el-descriptions-item label="是否重复">{{ reportResult.duplicate ? '是' : '否' }}</el-descriptions-item>
              <el-descriptions-item label="事件 ID">{{ reportResult.event_id || '--' }}</el-descriptions-item>
              <el-descriptions-item label="意向等级">{{ reportResult.intent_level || '--' }}</el-descriptions-item>
              <el-descriptions-item label="线索分">{{ formatNullable(reportResult.lead_score) }}</el-descriptions-item>
              <el-descriptions-item label="线索等级">{{ reportResult.lead_grade || '--' }}</el-descriptions-item>
              <el-descriptions-item label="TikTok 事件">{{ reportResult.tiktok_event || '--' }}</el-descriptions-item>
              <el-descriptions-item label="TikTok 启用">{{ reportResult.tiktok_enabled ? '是' : '否' }}</el-descriptions-item>
              <el-descriptions-item label="identity_key">{{ reportResult.identity_key || '--' }}</el-descriptions-item>
            </el-descriptions>
          </section>
        </aside>
      </article>
    </section>
  </AdminShell>
</template>

<script>
import {
  getLiveIntentAnalytics,
  getLiveIntentMonitoring,
  reportLiveBehaviorEvent,
} from '@/api'
import AdminShell from '@/components/admin/AdminShell.vue'

const EVENT_OPTIONS = [
  { value: 'page_view', label: 'page_view - 页面访问' },
  { value: 'scroll_depth', label: 'scroll_depth - 滚动深度' },
  { value: 'cta_click', label: 'cta_click - CTA 点击' },
  { value: 'deep_engagement_180s', label: 'deep_engagement_180s - 停留 180 秒' },
  { value: 'contact', label: 'contact - 联系/表单/WhatsApp' },
  { value: 'qualified_lead', label: 'qualified_lead - 高意向线索' },
]

function createDefaultFilters() {
  const end = new Date()
  const start = new Date(end.getTime() - 24 * 60 * 60 * 1000)
  return {
    range: [formatDateTime(start), formatDateTime(end)],
    room_id: '',
    content_name: '',
    utm_campaign: '',
    utm_source: '',
    intent_level: '',
  }
}

function createDefaultReportForm() {
  return {
    event: 'cta_click',
    event_id: createEventId(),
    session_id: '',
    user_id: '',
    room_id: '',
    content_name: '',
    content_id: '',
    page_url: '',
    referrer: '',
    utm_source: 'tiktok',
    utm_campaign: '',
    engagement_time: '',
    scroll_percent: '',
  }
}

function createEventId() {
  const random = Math.random().toString(36).slice(2, 8)
  return `evt_${Date.now()}_${random}`
}

function formatDateTime(date) {
  const pad = value => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

function toNumber(value) {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

function sumBy(items, key) {
  return items.reduce((total, item) => total + toNumber(item && item[key]), 0)
}

export default {
  name: 'LiveBehaviorAnalysis',
  components: {
    AdminShell,
  },
  data() {
    return {
      analyticsLoading: false,
      monitoringLoading: false,
      reporting: false,
      analyticsError: '',
      monitoringError: '',
      reportError: '',
      analyticsItems: [],
      monitoring: {},
      reportResult: null,
      filters: createDefaultFilters(),
      reportForm: createDefaultReportForm(),
      eventOptions: EVENT_OPTIONS,
      intentLevels: ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
      reportRules: {
        event: [{ required: true, message: '请选择事件', trigger: 'change' }],
        event_id: [{ required: true, message: '请输入事件 ID', trigger: 'blur' }],
      },
    }
  },
  computed: {
    kpiItems() {
      const sessions = sumBy(this.analyticsItems, 'session_count')
      const visitors = sumBy(this.analyticsItems, 'visitor_count')
      const qualifiedLeads = sumBy(this.analyticsItems, 'qualified_lead_count')
      const contacts = sumBy(this.analyticsItems, 'contact_count')
      const scoreWeight = this.analyticsItems.reduce((total, item) => {
        const count = toNumber(item.session_count) || 1
        return total + toNumber(item.avg_lead_score) * count
      }, 0)
      const scoreWeightBase = this.analyticsItems.reduce((total, item) => {
        return total + (toNumber(item.session_count) || 1)
      }, 0)
      const avgScore = scoreWeightBase > 0 ? scoreWeight / scoreWeightBase : null

      return [
        { key: 'sessions', label: '会话数', value: this.formatNumber(sessions) },
        { key: 'visitors', label: '访客数', value: this.formatNumber(visitors) },
        { key: 'qualified', label: '高意向线索', value: this.formatNumber(qualifiedLeads) },
        { key: 'avgScore', label: '平均线索分', value: this.formatDecimal(avgScore) },
        { key: 'intentRate', label: '意向率', value: this.formatPercent(sessions > 0 ? qualifiedLeads / sessions : null) },
        { key: 'contactRate', label: '联系率', value: this.formatPercent(sessions > 0 ? contacts / sessions : null) },
        { key: 'events', label: '事件总量', value: this.formatNumber(this.monitoring.total_events) },
        { key: 'duplicateRate', label: '重复率', value: this.formatPercent(this.monitoring.event_id_duplicate_rate) },
        { key: 'tiktokRate', label: 'TikTok 成功率', value: this.formatPercent(this.monitoring.tiktok_success_rate) },
      ]
    },
    monitoringItems() {
      return [
        { key: 'total_events', label: '原始行为事件数', value: this.formatNumber(this.monitoring.total_events) },
        { key: 'duplicate_attempts', label: '重复 event_id 请求', value: this.formatNumber(this.monitoring.duplicate_attempts) },
        { key: 'event_id_duplicate_rate', label: '重复率', value: this.formatPercent(this.monitoring.event_id_duplicate_rate) },
        { key: 'page_view_events', label: 'PageView 事件', value: this.formatNumber(this.monitoring.page_view_events) },
        { key: 'contact_events', label: 'Contact 事件', value: this.formatNumber(this.monitoring.contact_events) },
        { key: 'deep_180_events', label: '180s 事件', value: this.formatNumber(this.monitoring.deep_180_events) },
        { key: 'tiktok_sent', label: 'TikTok 成功', value: this.formatNumber(this.monitoring.tiktok_sent) },
        { key: 'tiktok_failed', label: 'TikTok 失败', value: this.formatNumber(this.monitoring.tiktok_failed) },
        { key: 'tiktok_success_rate', label: 'TikTok 成功率', value: this.formatPercent(this.monitoring.tiktok_success_rate) },
      ]
    },
  },
  mounted() {
    this.refreshData()
  },
  methods: {
    buildQuery() {
      const query = {}
      const [from, to] = this.filters.range || []
      if (from) query.from = from
      if (to) query.to = to
      ;['room_id', 'content_name', 'utm_campaign', 'utm_source', 'intent_level'].forEach(key => {
        if (this.filters[key]) query[key] = this.filters[key]
      })
      return query
    },
    buildMonitoringQuery() {
      const query = {}
      const [from, to] = this.filters.range || []
      if (from) query.from = from
      if (to) query.to = to
      return query
    },
    refreshData() {
      this.fetchAnalytics()
      this.fetchMonitoring()
    },
    async fetchAnalytics() {
      this.analyticsLoading = true
      this.analyticsError = ''
      try {
        const res = await getLiveIntentAnalytics(this.buildQuery())
        this.analyticsItems = Array.isArray(res?.data?.items) ? res.data.items : []
      } catch (error) {
        this.analyticsItems = []
        this.analyticsError = error?.message || '直播意向分析加载失败'
        this.$message.error(this.analyticsError)
      } finally {
        this.analyticsLoading = false
      }
    },
    async fetchMonitoring() {
      this.monitoringLoading = true
      this.monitoringError = ''
      try {
        const res = await getLiveIntentMonitoring(this.buildMonitoringQuery())
        this.monitoring = res && res.data && typeof res.data === 'object' ? res.data : {}
      } catch (error) {
        this.monitoring = {}
        this.monitoringError = error?.message || '直播意向监控加载失败'
        this.$message.error(this.monitoringError)
      } finally {
        this.monitoringLoading = false
      }
    },
    resetFilters() {
      this.filters = createDefaultFilters()
      this.refreshData()
    },
    generateReportEventId() {
      this.reportForm.event_id = createEventId()
    },
    resetReportForm() {
      this.reportForm = createDefaultReportForm()
      this.reportResult = null
      this.reportError = ''
      this.$nextTick(() => {
        if (this.$refs.reportForm) this.$refs.reportForm.clearValidate()
      })
    },
    async submitReport() {
      this.reportError = ''
      this.reportResult = null
      const valid = await this.validateReportForm()
      if (!valid) return

      this.reporting = true
      try {
        const res = await reportLiveBehaviorEvent(this.buildReportPayload())
        this.reportResult = res && res.data ? res.data : {}
        this.$message.success(this.reportResult.duplicate ? '重复事件已返回' : '行为事件上报成功')
      } catch (error) {
        this.reportError = error?.message || '行为事件上报失败'
        this.$message.error(this.reportError)
      } finally {
        this.reporting = false
      }
    },
    validateReportForm() {
      return new Promise(resolve => {
        this.$refs.reportForm.validate(valid => {
          if (!valid) {
            resolve(false)
            return
          }
          if (!this.reportForm.session_id && !this.reportForm.user_id) {
            this.reportError = 'session_id 和 user_id 至少填写一个'
            this.$message.error(this.reportError)
            resolve(false)
            return
          }
          resolve(true)
        })
      })
    },
    buildReportPayload() {
      const payload = {}
      Object.entries(this.reportForm).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          payload[key] = value
        }
      })
      ;['engagement_time', 'scroll_percent'].forEach(key => {
        if (payload[key] !== undefined) payload[key] = Number(payload[key])
      })
      payload.event_time = Date.now()
      return payload
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '--'
      const number = Number(value)
      if (!Number.isFinite(number)) return '--'
      return number.toLocaleString()
    },
    formatDecimal(value) {
      if (value === null || value === undefined || value === '') return '--'
      const number = Number(value)
      if (!Number.isFinite(number)) return '--'
      return number.toFixed(1)
    },
    formatPercent(value) {
      if (value === null || value === undefined || value === '') return '--'
      const number = Number(value)
      if (!Number.isFinite(number)) return '--'
      return `${(number * 100).toFixed(1)}%`
    },
    formatNullable(value) {
      return value === null || value === undefined || value === '' ? '--' : value
    },
  },
}
</script>

<style scoped>
.behavior-analysis {
  display: grid;
  gap: 18px;
}

.filter-panel,
.panel,
.kpi-card {
  border: 1px solid #dbe4f0;
  background: rgba(255, 255, 255, 0.96);
}

.filter-panel {
  padding: 18px 22px 4px;
  border-radius: 20px;
}

.filter-form {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 0 12px;
}

.filter-form ::v-deep .el-form-item {
  margin-right: 0;
}

.filter-form ::v-deep .el-date-editor {
  width: 360px;
  max-width: 100%;
}

.filter-form ::v-deep .el-input,
.filter-form ::v-deep .el-select {
  width: 190px;
}

.filter-actions {
  white-space: nowrap;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(9, minmax(120px, 1fr));
  gap: 12px;
}

.kpi-card {
  min-height: 92px;
  padding: 16px;
  border-radius: 16px;
  display: grid;
  align-content: center;
  gap: 10px;
}

.kpi-card span,
.monitor-item span {
  color: #64748b;
  font-size: 13px;
}

.kpi-card strong {
  color: #0f172a;
  font-size: 24px;
  line-height: 1.1;
}

.content-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 420px;
  gap: 18px;
  align-items: start;
}

.side-stack {
  display: grid;
  gap: 18px;
}

.panel {
  border-radius: 24px;
  overflow: hidden;
}

.panel-main {
  min-width: 0;
}

.panel-header {
  padding: 20px 22px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  border-bottom: 1px solid #edf2f7;
}

.panel-header h2 {
  margin: 0;
  color: #0f172a;
  font-size: 18px;
  line-height: 1.3;
}

.panel-header p {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.5;
}

.panel-alert {
  margin: 16px 18px 0;
  width: auto;
}

.analysis-table {
  width: 100%;
}

.monitor-grid {
  min-height: 180px;
  padding: 18px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.monitor-item {
  padding: 14px;
  border: 1px solid #e5eaf2;
  border-radius: 14px;
  background: #f8fafc;
  display: grid;
  gap: 8px;
}

.monitor-item strong {
  color: #0f172a;
  font-size: 20px;
}

.report-form {
  padding: 18px;
}

.report-form ::v-deep .el-form-item {
  margin-bottom: 16px;
}

.full-field {
  width: 100%;
}

.report-pair {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.report-actions {
  display: flex;
  gap: 10px;
}

.report-result {
  margin: 0 18px 18px;
  width: auto;
}

@media (max-width: 1440px) {
  .kpi-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .content-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .filter-panel {
    padding: 16px 16px 2px;
  }

  .filter-form {
    display: block;
  }

  .filter-form ::v-deep .el-form-item,
  .filter-form ::v-deep .el-input,
  .filter-form ::v-deep .el-select,
  .filter-form ::v-deep .el-date-editor {
    width: 100%;
  }

  .kpi-grid,
  .monitor-grid,
  .report-pair {
    grid-template-columns: 1fr;
  }

  .panel-header {
    padding: 16px;
    flex-direction: column;
  }

  .report-form {
    padding: 16px;
  }
}
</style>
